import { PDFDocument, rgb, PDFPage } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import type { FieldOverlay } from "./pages/case/formFields";
import { getBlob } from "./blobs";
import {
  PDF_ZONE_POSITIONS,
  TRAUMA_CROSS_POSITIONS,
} from "./pages/body-diagram";
import type { TraumaMap } from "../../shared/src/commands";
import { db } from "./db";
import * as s from "../../shared/src/schema";
import { eq } from "drizzle-orm";

// Vite serves the PDF as a static URL (not bundled into JS)
import pdfUrl from "./assets/001.pdf?url";

const PAGE_W = 595.276;
const PAGE_H = 841.89;
const FONT_SIZE = 5.8;
const CW_PCT = 0.8685;
const CH_PCT = 0.6141;

// Injury marks use crop-relative coordinates (same as PdfForm.tsx)
const INJURY_CROP = { x: 432, y: 159, w: 152, h: 151 };

function pdfX(xPct: number): number {
  return (xPct / 100) * PAGE_W;
}

function pdfY(yPct: number): number {
  return PAGE_H - (yPct / 100) * PAGE_H;
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function fmtDateFromTimestamp(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

function fmtDateFromString(v: string): string {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  return v;
}

export async function exportPdf(
  data: any,
  fields: FieldOverlay[],
  traumaMap: TraumaMap,
  isAnon = false,
): Promise<Uint8Array> {
  // Fetch PDF template and font
  const [pdfBytes, fontBytes] = await Promise.all([
    fetch(pdfUrl).then((r) => r.arrayBuffer()),
    fetch("/fonts/e-Ukraine-Regular.otf").then((r) => r.arrayBuffer()),
  ]);

  const pdfDoc = await PDFDocument.load(pdfBytes);
  pdfDoc.registerFontkit(fontkit);
  const font = await pdfDoc.embedFont(fontBytes);

  const pages = pdfDoc.getPages();
  const page1 = pages[0];
  const page2 = pages[1];

  for (const field of fields) {
    try {
      if (isAnon && field.isPii) continue;

      const value = field.getValue(data);
      if (value == null || value === "" || value === false) continue;

      const page = field.page === 1 ? page1 : page2;
      const x = pdfX(field.x);
      const fieldW = (field.w / 100) * PAGE_W;
      const fieldH = (field.h / 100) * PAGE_H;

      switch (field.type) {
        case "checkbox": {
          if (!value) break;
          const w = (CW_PCT / 100) * PAGE_W;
          const h = (CH_PCT / 100) * PAGE_H;
          // field.y is rect top; PDF rect needs bottom-left origin
          const y = pdfY(field.y) - h;
          page.drawRectangle({
            x,
            y,
            width: w,
            height: h,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "text": {
          const text = String(value);
          // Truncate if too wide
          let display = text;
          while (
            display.length > 0 &&
            font.widthOfTextAtSize(display, FONT_SIZE) > fieldW
          ) {
            display = display.slice(0, -1);
          }
          page.drawText(display, {
            x,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "patient-name": {
          const text = isAnon ? "—" : String(value);
          let display = text;
          while (
            display.length > 0 &&
            font.widthOfTextAtSize(display, FONT_SIZE) > fieldW
          ) {
            display = display.slice(0, -1);
          }
          page.drawText(display, {
            x,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "number": {
          const text = String(value);
          const tw = font.widthOfTextAtSize(text, FONT_SIZE);
          const cx = x + (fieldW - tw) / 2;
          page.drawText(text, {
            x: cx,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "select": {
          const opt = field.options?.find((o) => o.value === String(value));
          const text = opt ? opt.label : String(value);
          const tw = font.widthOfTextAtSize(text, FONT_SIZE);
          const cx = x + (fieldW - tw) / 2;
          page.drawText(text, {
            x: cx,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "blood-rh": {
          const opt = field.options?.find((o) => o.value === String(value));
          const text = opt ? opt.label : String(value);
          const tw = font.widthOfTextAtSize(text, FONT_SIZE);
          const cx = x + (fieldW - tw) / 2;
          page.drawText(text, {
            x: cx,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "vision": {
          const text = String(value);
          const tw = font.widthOfTextAtSize(text, FONT_SIZE);
          const cx = x + (fieldW - tw) / 2;
          page.drawText(text, {
            x: cx,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "time": {
          const ts = value as number;
          if (!ts) break;
          const text = fmtTime(ts);
          const tw = font.widthOfTextAtSize(text, FONT_SIZE);
          const cx = x + (fieldW - tw) / 2;
          page.drawText(text, {
            x: cx,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "datetime-date": {
          const ts = value as number;
          if (!ts) break;
          const text = fmtDateFromTimestamp(ts);
          const tw = font.widthOfTextAtSize(text, FONT_SIZE);
          const cx = x + (fieldW - tw) / 2;
          page.drawText(text, {
            x: cx,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "date": {
          const text = fmtDateFromString(String(value));
          const tw = font.widthOfTextAtSize(text, FONT_SIZE);
          const cx = x + (fieldW - tw) / 2;
          page.drawText(text, {
            x: cx,
            y: pdfY(field.y),
            size: FONT_SIZE,
            font,
            color: rgb(0, 0, 0),
          });
          break;
        }

        case "textarea": {
          const text = String(value);
          const indent = field.textIndent
            ? (field.textIndent / 100) * PAGE_W
            : 0;
          const lh = field.lineHeight
            ? (field.lineHeight / 100) * PAGE_H
            : FONT_SIZE * 1.4;
          const cutoff = field.lastLineCutoff
            ? (field.lastLineCutoff / 100) * PAGE_W
            : 0;

          // Word-wrap text into lines
          const words = text.split(/\s+/);
          const lines: string[] = [];
          let currentLine = "";

          for (const word of words) {
            if (!word) continue;
            const isFirstLine = lines.length === 0;
            const availW = fieldW - (isFirstLine ? indent : 0);
            const test = currentLine ? `${currentLine} ${word}` : word;
            if (
              font.widthOfTextAtSize(test, FONT_SIZE) > availW &&
              currentLine
            ) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = test;
            }
          }
          if (currentLine) lines.push(currentLine);

          // Calculate max lines that fit
          const maxLines = Math.max(1, Math.floor(fieldH / lh));

          for (let i = 0; i < Math.min(lines.length, maxLines); i++) {
            const isFirst = i === 0;
            const isLast = i === Math.min(lines.length, maxLines) - 1;
            const lineX = x + (isFirst ? indent : 0);
            const lineY = pdfY(field.y) - i * lh;

            let line = lines[i];
            // Truncate last line if cutoff
            const availW =
              fieldW - (isFirst ? indent : 0) - (isLast ? cutoff : 0);
            while (
              line.length > 0 &&
              font.widthOfTextAtSize(line, FONT_SIZE) > availW
            ) {
              line = line.slice(0, -1);
            }

            page.drawText(line, {
              x: lineX,
              y: lineY,
              size: FONT_SIZE,
              font,
              color: rgb(0, 0, 0),
            });
          }
          break;
        }

        case "signature": {
          const sha256 = value as string;
          if (!sha256) break;
          const blob = await getBlob(sha256);
          if (!blob) break;
          const pngImage = await pdfDoc.embedPng(blob.data);
          const dims = pngImage.scaleToFit(fieldW, fieldH);
          // Center the signature within the field bounds
          const imgX = x + (fieldW - dims.width) / 2;
          // field.y is baseline; place image so its center aligns vertically
          const imgY = pdfY(field.y) - dims.height / 2;
          page.drawImage(pngImage, {
            x: imgX,
            y: imgY,
            width: dims.width,
            height: dims.height,
          });
          break;
        }
      }
    } catch (e) {
      console.warn(`exportPdf: failed to render field ${field.id}:`, e);
    }
  }

  // ─── Body diagram: burns, traumas, injury marks (page 1 only) ─────
  drawTraumaMap(page1, traumaMap);

  return pdfDoc.save();
}

/** Draw a 45° rotated cross mark at (cx, cy) with given half-size */
function drawCross(
  page: PDFPage,
  cx: number,
  cy: number,
  size: number,
  color = rgb(0.145, 0.388, 0.922),
) {
  const thick = size * 0.3;
  // Horizontal bar of the cross (before rotation)
  // For a 45° rotated cross we draw two thin rectangles rotated ±45°
  // pdf-lib doesn't support rotation on drawRectangle, so draw as two line segments
  const d = size * 0.7; // half-length of each bar
  // Bar 1: top-left to bottom-right
  page.drawLine({
    start: { x: cx - d, y: cy + d },
    end: { x: cx + d, y: cy - d },
    thickness: thick,
    color,
  });
  // Bar 2: bottom-left to top-right
  page.drawLine({
    start: { x: cx - d, y: cy - d },
    end: { x: cx + d, y: cy + d },
    thickness: thick,
    color,
  });
}

function drawTraumaMap(page: PDFPage, traumaMap: TraumaMap) {
  const BURN_COLOR = rgb(0.863, 0.149, 0.149); // #dc2626
  const CROSS_COLOR = rgb(0.145, 0.388, 0.922); // #2563eb

  // Burns: red circle outlines
  if (traumaMap.burns.length) {
    const burnSet = new Set(traumaMap.burns);
    const r = PAGE_W * 0.012; // matches PdfForm radius
    for (const z of PDF_ZONE_POSITIONS) {
      if (!burnSet.has(z.id)) continue;
      const cx = (z.xPct / 100) * PAGE_W;
      const cy = PAGE_H - (z.yPct / 100) * PAGE_H;
      page.drawCircle({
        x: cx,
        y: cy,
        size: r,
        borderColor: BURN_COLOR,
        borderWidth: 1.5,
        color: undefined,
      });
    }
  }

  // Trauma crosses: blue X marks at predefined positions
  if (traumaMap.traumas.length) {
    const traumaSet = new Set(traumaMap.traumas);
    const size = PAGE_W * 0.008;
    for (const z of TRAUMA_CROSS_POSITIONS) {
      if (!traumaSet.has(z.id)) continue;
      const cx = (z.xPct / 100) * PAGE_W;
      const cy = PAGE_H - (z.yPct / 100) * PAGE_H;
      drawCross(page, cx, cy, size, CROSS_COLOR);
    }
  }

  // Injury marks: blue X marks at free-form crop-relative positions
  if (traumaMap.injuries?.length) {
    const size = PAGE_W * 0.008;
    for (const m of traumaMap.injuries) {
      // Convert crop-relative % to full-SVG points (SVG_W/H === PAGE_W/H)
      const cx = INJURY_CROP.x + (m.x / 100) * INJURY_CROP.w;
      const cy = PAGE_H - (INJURY_CROP.y + (m.y / 100) * INJURY_CROP.h);
      drawCross(page, cx, cy, size, CROSS_COLOR);
    }
  }
}

export async function loadCaseForExport(caseId: string) {
  const c = await db.select().from(s.cases).where(eq(s.cases.id, caseId));
  if (!c[0]) return null;
  const [
    p,
    patientCases,
    vitals,
    ventilation,
    diagnostics,
    labValues,
    exam,
    cpr,
    tourniquets,
    medications,
    surgeries,
    atts,
    hospitals,
    units,
  ] = await Promise.all([
    db.select().from(s.patients).where(eq(s.patients.id, c[0].patientId)),
    c[0].patientId
      ? db.select({ id: s.cases.id }).from(s.cases).where(eq(s.cases.patientId, c[0].patientId))
      : Promise.resolve([]),
    db.select().from(s.caseVitals).where(eq(s.caseVitals.caseId, caseId)),
    db.select().from(s.caseVentilation).where(eq(s.caseVentilation.caseId, caseId)),
    db.select().from(s.caseDiagnostics).where(eq(s.caseDiagnostics.caseId, caseId)),
    db.select().from(s.caseLabValues).where(eq(s.caseLabValues.caseId, caseId)),
    db.select().from(s.caseExam).where(eq(s.caseExam.caseId, caseId)),
    db.select().from(s.caseCpr).where(eq(s.caseCpr.caseId, caseId)),
    db.select().from(s.caseTourniquet).where(eq(s.caseTourniquet.caseId, caseId)),
    db.select().from(s.caseMedication).where(eq(s.caseMedication.caseId, caseId)),
    db.select().from(s.caseSurgery).where(eq(s.caseSurgery.caseId, caseId)),
    db.select().from(s.attachments).where(eq(s.attachments.caseId, caseId)),
    db.select().from(s.hospitals).where(eq(s.hospitals.archived, 0)),
    db.select().from(s.units).where(eq(s.units.archived, 0)),
  ]);
  return {
    case: c[0],
    patient: p[0] || null,
    patientCaseCount: patientCases.length,
    vitals,
    ventilation,
    diagnostics,
    labValues,
    exam,
    cpr,
    tourniquets,
    medications,
    surgeries,
    hospitals,
    units,
    attachments: atts.filter((a) => !a.deleted),
  };
}

export async function batchExportPdf(
  caseIds: string[],
  fields: FieldOverlay[],
): Promise<Uint8Array> {
  const allBytes: Uint8Array[] = [];
  for (const id of caseIds) {
    const data = await loadCaseForExport(id);
    if (!data) continue;
    const rawTrauma = data.case.traumaLocations;
    const traumaMap: TraumaMap =
      rawTrauma &&
      !Array.isArray(rawTrauma) &&
      Array.isArray((rawTrauma as TraumaMap).burns)
        ? { ...(rawTrauma as TraumaMap), injuries: (rawTrauma as TraumaMap).injuries ?? [] }
        : { burns: [], traumas: [], injuries: [], arrows: [] };
    allBytes.push(await exportPdf(data, fields, traumaMap));
  }
  const merged = await PDFDocument.create();
  for (const bytes of allBytes) {
    const doc = await PDFDocument.load(bytes);
    const pages = await merged.copyPages(doc, doc.getPageIndices());
    pages.forEach((p) => merged.addPage(p));
  }
  return merged.save();
}
